// src/components/dashboard/PreventedLossAnalytics.jsx — v2.2 / Task A4
//
// Prevented Loss Analytics — усиливает главный KPI продукта.
// NO backend changes. Aggregates from existing auto_actions[] array.
//
// Shows:
//   - Cumulative prevented loss (all-time)
//   - Prevented loss last 7d
//   - Per-incident breakdown (top rescued anomalies, sorted by prevented_loss)
//   - Inline prevented_loss in each incident row

const MONO = "var(--font-mono)";

function fmtMoney(v) {
  const n = Math.round(Number(v) || 0);
  return n > 0 ? `€ ${n.toLocaleString("en-US")}` : "€ 0";
}

function fmtDate(ts) {
  if (!ts) return "—";
  const n = Number(ts);
  return new Date(n > 1e12 ? n : n * 1000)
    .toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const ACTION_LABELS = {
  reduce_spend:    "Spend reduced",
  limit_budget:    "Budget limited",
  pause_campaigns: "Campaigns paused",
  safe_mode:       "Safe mode",
  monitor:         "Monitoring",
};

// ── IncidentRow ───────────────────────────────────────────
function IncidentRow({ action, rank }) {
  const type      = action.action_type ?? "monitor";
  const label     = ACTION_LABELS[type] ?? type.replace(/_/g, " ");
  const prevented = Number(action.prevented_loss) || 0;
  const date      = fmtDate(action.timestamp);
  const ctx       = (() => { try { return JSON.parse(action.context ?? "{}"); } catch { return {}; } })();
  const risk      = ctx.risk_status ?? action.risk_status ?? "—";

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "24px 1fr auto auto",
      alignItems: "center",
      gap: 12,
      padding: "10px 16px",
      borderBottom: "1px solid var(--border-subtle)",
    }}>
      {/* Rank */}
      <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", textAlign: "right" }}>
        #{rank}
      </div>

      {/* Action + date */}
      <div>
        <div style={{ fontFamily: MONO, fontSize: 12, color: "var(--text-primary)", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)" }}>
          {date} · risk: {risk}
        </div>
      </div>

      {/* Prevented loss */}
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", marginBottom: 2 }}>
          prevented
        </div>
        <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "var(--success)" }}>
          {fmtMoney(prevented)}
        </div>
      </div>

      {/* Bar indicator */}
      <div style={{ width: 40 }}>
        <div style={{ height: 4, borderRadius: 2, background: "var(--border-subtle)", overflow: "hidden" }}>
          <div style={{
            height: "100%", background: "var(--success)",
            width: `${Math.min(100, prevented / 50)}%`,
            borderRadius: 2,
          }} />
        </div>
      </div>
    </div>
  );
}

// ── PreventedLossAnalytics ────────────────────────────────
/**
 * @param {{
 *   actions: object[],   — from /api/antiloss/actions
 *   loading?: boolean,
 * }} props
 */
export default function PreventedLossAnalytics({ actions = [], loading }) {
  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton" style={{ height: 48, borderRadius: "var(--radius)" }} />
      ))}
    </div>
  );

  // All actions sorted by prevented_loss descending
  const sorted = [...actions]
    .filter(a => Number(a.prevented_loss) > 0)
    .sort((a, b) => Number(b.prevented_loss) - Number(a.prevented_loss));

  // Totals
  const total_all = sorted.reduce((s, a) => s + Number(a.prevented_loss), 0);

  const cutoff7d  = Date.now() / 1000 - 7 * 86400;
  const total_7d  = sorted
    .filter(a => Number(a.timestamp) >= cutoff7d)
    .reduce((s, a) => s + Number(a.prevented_loss), 0);

  if (!sorted.length) {
    return (
      <div style={{ padding: "14px 18px", borderRadius: "var(--radius)",
                    background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
                    fontFamily: MONO, fontSize: 11, color: "var(--text-muted)" }}>
        No protection events recorded yet
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Summary row — A4: cumulative + 7d */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{
          padding: "12px 16px", borderRadius: "var(--radius)",
          background: "var(--success-bg)", border: "1px solid var(--success-border)",
        }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.12em",
                        textTransform: "uppercase", color: "var(--success)", opacity: 0.8, marginBottom: 5 }}>
            Total prevented (all-time)
          </div>
          <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: "var(--success)", lineHeight: 1 }}>
            {fmtMoney(total_all)}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", marginTop: 4 }}>
            across {sorted.length} incident{sorted.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div style={{
          padding: "12px 16px", borderRadius: "var(--radius)",
          background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.12em",
                        textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 5 }}>
            Saved last 7 days
          </div>
          <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700,
                        color: total_7d > 0 ? "var(--success)" : "var(--text-muted)", lineHeight: 1 }}>
            {fmtMoney(total_7d)}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", marginTop: 4 }}>
            {total_7d > 0 ? "active protection" : "no recent incidents"}
          </div>
        </div>
      </div>

      {/* Top incidents table — A4 requirement */}
      <div style={{
        borderRadius: "var(--radius)", background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)", overflow: "hidden",
      }}>
        <div style={{
          padding: "10px 16px", borderBottom: "1px solid var(--border-subtle)",
          fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "var(--text-muted)",
        }}>
          Top rescued incidents · sorted by prevented loss
        </div>
        {sorted.slice(0, 8).map((a, i) => (
          <IncidentRow key={a.id ?? i} action={a} rank={i + 1} />
        ))}
      </div>

    </div>
  );
}
